import React, { useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, SkipForward } from 'lucide-react';
import { useTutorial, TutorialStep } from '../context/TutorialContext';
import { TutorialHighlight } from './TutorialHighlight';

export const TutorialOverlay: React.FC = () => {
  const {
    isActive,
    currentStep,
    nextStep,
    prevStep,
    skipTutorial,
    endTutorial
  } = useTutorial();

  const [overlayPosition, setOverlayPosition] = useState({ top: 0, left: 0 });

  // Calculate optimal overlay position based on highlighted element
  useEffect(() => {
    if (!isActive || !currentStep?.highlightSelector) {
      // Center the overlay if no element to highlight
      setOverlayPosition({
        top: window.innerHeight / 2 - 150,
        left: window.innerWidth / 2 - 200
      });
      return;
    }

    const element = document.querySelector(currentStep.highlightSelector);
    if (element) {
      const rect = element.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let top = 0;
      let left = 0;

      // Position based on step preference or auto-calculate
      switch (currentStep.position) {
        case 'top':
          top = Math.max(20, rect.top - 320);
          left = Math.max(20, Math.min(rect.left, viewportWidth - 420));
          break;
        case 'bottom':
          top = Math.min(viewportHeight - 320, rect.bottom + 20);
          left = Math.max(20, Math.min(rect.left, viewportWidth - 420));
          break;
        case 'left':
          top = Math.max(20, rect.top - 50);
          left = Math.max(20, rect.left - 420);
          break;
        case 'right':
          top = Math.max(20, rect.top - 50);
          left = Math.min(viewportWidth - 20, rect.right + 20);
          break;
        default: // center or auto
          top = Math.max(20, rect.top + rect.height / 2 - 150);
          left = Math.max(20, Math.min(rect.left + rect.width / 2 - 200, viewportWidth - 420));
      }

      setOverlayPosition({ top, left });
    }
  }, [currentStep, isActive]);

  if (!isActive || !currentStep) {
    return null;
  }

  return (
    <>
      {/* Semi-transparent overlay */}
      <div className="fixed inset-0 bg-black/30 z-40" onClick={(e) => e.stopPropagation()} />

      {/* Tutorial Highlight */}
      <TutorialHighlight step={currentStep} isActive={isActive} />

      {/* Tutorial Content Panel */}
      <div
        className="fixed z-50 bg-gray-900 border-2 border-yellow-500 rounded-lg shadow-2xl p-6 max-w-sm"
        style={{
          top: overlayPosition.top,
          left: overlayPosition.left,
          minWidth: '400px'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-xl font-bold text-yellow-500">
            {currentStep.title}
          </h3>
          <button
            onClick={endTutorial}
            className="text-gray-400 hover:text-white p-1"
            title="Close Tutorial"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="text-gray-200 mb-6 whitespace-pre-line">
          {currentStep.content}
        </div>

        {/* Action Indicator */}
        {currentStep.actionRequired && currentStep.actionRequired !== 'none' && (
          <div className="bg-yellow-900/20 border border-yellow-600 rounded p-3 mb-4">
            <div className="text-yellow-300 text-sm font-semibold">
              {currentStep.actionRequired === 'click' && '👆 Click the highlighted element to continue'}
              {currentStep.actionRequired === 'navigate' && '🧭 Navigate to the highlighted section'}
              {currentStep.actionRequired === 'input' && '✏️ Fill in the highlighted field'}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <div className="flex gap-2">
            {currentStep.prevStepId && (
              <button
                onClick={prevStep}
                className="flex items-center gap-1 bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-2 rounded text-sm"
              >
                <ChevronLeft size={16} />
                Previous
              </button>
            )}

            {currentStep.canSkip && (
              <button
                onClick={skipTutorial}
                className="flex items-center gap-1 bg-gray-700 hover:bg-gray-600 text-gray-300 px-3 py-2 rounded text-sm"
              >
                <SkipForward size={16} />
                Skip Tutorial
              </button>
            )}
          </div>

          <button
            onClick={currentStep.nextStepId ? nextStep : endTutorial}
            className="flex items-center gap-1 bg-yellow-600 hover:bg-yellow-500 text-white px-4 py-2 rounded font-semibold"
          >
            {currentStep.nextStepId ? (
              <>
                Next
                <ChevronRight size={16} />
              </>
            ) : (
              'Finish Tutorial'
            )}
          </button>
        </div>
      </div>
    </>
  );
};
